"use client"

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { navigateWithThreadPreservation } from '@/utils/thread-navigation'
import { formatDistanceToNow } from 'date-fns'
import { ExternalLink, Megaphone, RefreshCw, Settings2, ThumbsDown, ThumbsUp } from 'lucide-react'
import { toast } from 'sonner'
import { getPageHeader } from '../../../constants'
import {
  useAdminFeedbackItems,
  useAdminMessageRatings,
  useAdminRetryLinearSync,
  useAdminUpdateFeedbackItem,
  usePromoteMessageRating,
  type FeedbackAdminItem,
  type FeedbackStatus,
  type MessageRatingAdmin,
} from '../../../hooks/use-feedback'
import { Badge } from '../../ui/badge'
import { Button } from '../../ui/button'
import { Card, CardContent } from '../../ui/card'
import { ListCard } from '../../list-card'
import { CardTitle } from '../../ui/card'
import { PageContainer } from '../../ui/page-container'
import { PageHeader } from '../../ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { PageHeaderStack } from '@/components/navigation/page-header-stack'
import {
  PageHeaderActionPrimary,
  PageHeaderActions,
} from '@/components/navigation/page-header-actions'
import { ListSortMenu } from '@/components/filters/list-sort-menu'
import { PaneResponsiveCardGrid } from '@/components/layout/pane-responsive-card-grid'
import { PaneListEmptyState } from '@/components/layout/pane-list-empty-state'
import { DashboardSectionSkeleton } from '@/components/dashboard/dashboard-section-skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs'
import {
  FEEDBACK_STATUSES,
  feedbackKindBadgeVariant,
  feedbackStatusBadgeVariant,
  formatFeedbackElicitation,
  formatFeedbackKind,
  formatFeedbackStatus,
  formatFeedbackVisibility,
} from '@/lib/feedback-display'
import { useAdminBasePath, adminPath } from '@/components/admin/use-admin-base-path'
import { useListFilterBar } from '@/hooks/use-list-filter-bar'
import {
  computeFeedbackKindCounts,
  FEEDBACK_KIND_FILTER_DEFINITIONS,
  FEEDBACK_SORT_OPTIONS,
  type FeedbackKindFilterState,
  type FeedbackSort,
} from '@/lib/list-filters/feedback-kind'

const LINEAR_PRIORITY_LABELS: Record<number, string> = {
  0: 'No priority',
  1: 'Urgent',
  2: 'High',
  3: 'Normal',
  4: 'Low',
}

function AdminFeedbackCard({
  item,
  onStatusChange,
  onRetryLinear,
  isUpdating,
  isRetrying,
}: {
  item: FeedbackAdminItem
  onStatusChange: (item: FeedbackAdminItem, status: FeedbackStatus) => void
  onRetryLinear: (item: FeedbackAdminItem) => void
  isUpdating: boolean
  isRetrying: boolean
}) {
  const elicitationLabel = formatFeedbackElicitation(item.elicitation)
  const visibilityLabel = formatFeedbackVisibility(item.visibility)
  const linearHref = item.linear_url || null
  const priorityLabel =
    item.linear_priority != null ? LINEAR_PRIORITY_LABELS[item.linear_priority] : null
  const isPlatform = item.visibility === 'platform'
  const needsLinearSync = !isPlatform && !item.external_ref
  const linearSyncLabel =
    item.linear_sync_error || item.linear_sync_attempts > 0 ? 'Retry sync' : 'Push to Linear'

  return (
    <ListCard
      className="h-full"
      agentHighlight={`feedback:${item.id}`}
      header={
        <div className="flex min-w-0 flex-col gap-2">
          <div className="pointer-events-none flex flex-wrap items-center gap-1.5">
            <Badge variant={feedbackKindBadgeVariant(item.kind)}>
              {formatFeedbackKind(item.kind)}
            </Badge>
            <Badge variant={feedbackStatusBadgeVariant(item.status)}>
              {formatFeedbackStatus(item.status)}
            </Badge>
            {visibilityLabel ? <Badge variant="outline">{visibilityLabel}</Badge> : null}
            {elicitationLabel ? <Badge variant="outline">{elicitationLabel}</Badge> : null}
            {item.external_ref ? (
              <Badge variant="outline">{item.external_ref}</Badge>
            ) : null}
            {item.linear_sync_error ? (
              <Badge variant="destructive">Linear sync error</Badge>
            ) : null}
          </div>
          <CardTitle className="pointer-events-none min-w-0 break-words text-base">
            {item.title}
          </CardTitle>
        </div>
      }
      body={
        <div className="space-y-2">
          {item.body ? (
            <p className="line-clamp-3 text-sm text-muted-foreground">{item.body}</p>
          ) : null}
          <p className="text-xs text-muted-foreground">
            {item.reporter?.email ?? 'Unknown reporter'}
            {isPlatform
              ? null
              : ` · ${item.vote_count} ${item.vote_count === 1 ? 'vote' : 'votes'}`}
            {' · '}
            {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
          </p>
          {(priorityLabel || item.linear_assignee_email) && !isPlatform ? (
            <p className="text-xs text-muted-foreground">
              {priorityLabel ? `Linear: ${priorityLabel}` : null}
              {priorityLabel && item.linear_assignee_email ? ' · ' : null}
              {item.linear_assignee_email
                ? `Assignee ${item.linear_assignee_email}`
                : null}
            </p>
          ) : null}
          {item.linear_sync_error ? (
            <p className="line-clamp-2 text-xs text-destructive">{item.linear_sync_error}</p>
          ) : null}
        </div>
      }
      footer={
        <div className="pointer-events-auto flex flex-wrap items-center justify-end gap-2">
          {linearHref ? (
            <Button variant="outline" size="sm" className="h-8 text-xs" asChild>
              <a href={linearHref} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                Linear
              </a>
            </Button>
          ) : null}
          {needsLinearSync ? (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              disabled={isRetrying}
              onClick={() => onRetryLinear(item)}
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
              {linearSyncLabel}
            </Button>
          ) : null}
          <Select
            value={item.status}
            disabled={isUpdating}
            onValueChange={(value) => onStatusChange(item, value as FeedbackStatus)}
          >
            <SelectTrigger className="h-8 w-[160px] text-xs" aria-label="Set status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FEEDBACK_STATUSES.map(({ value, label }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      }
    />
  )
}

function RatingCard({
  rating,
  onPromote,
  isPromoting,
}: {
  rating: MessageRatingAdmin
  onPromote: (rating: MessageRatingAdmin) => void
  isPromoting: boolean
}) {
  const isDown = rating.score < 0.5
  const ScoreIcon = isDown ? ThumbsDown : ThumbsUp

  return (
    <ListCard
      className="h-full"
      header={
        <div className="flex min-w-0 flex-col gap-2">
          <div className="pointer-events-none flex flex-wrap items-center gap-1.5">
            <Badge variant={isDown ? 'destructiveSoft' : 'successSoft'}>
              <ScoreIcon className="mr-1 h-3 w-3" aria-hidden />
              {isDown ? 'Thumbs down' : 'Thumbs up'}
            </Badge>
            {rating.promoted_item_id ? (
              <Badge variant="outline">Promoted</Badge>
            ) : null}
            {rating.tags.map((tag) => (
              <Badge key={tag} variant="secondarySoft">
                {tag}
              </Badge>
            ))}
          </div>
          <CardTitle className="pointer-events-none min-w-0 break-words text-base">
            {rating.comment?.trim() || 'No comment'}
          </CardTitle>
        </div>
      }
      body={
        <div className="space-y-1 text-xs text-muted-foreground">
          <p>{rating.user?.email ?? 'Anonymous'} · run {rating.run_id.slice(0, 8)}…</p>
          {rating.created_at ? (
            <p>{formatDistanceToNow(new Date(rating.created_at), { addSuffix: true })}</p>
          ) : null}
        </div>
      }
      footer={
        <div className="pointer-events-auto flex justify-end">
          {rating.promoted_item_id ? (
            <Button variant="outline" size="sm" className="h-8 text-xs" disabled>
              Already on board
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              disabled={isPromoting}
              onClick={() => onPromote(rating)}
            >
              Promote to board
            </Button>
          )}
        </div>
      }
    />
  )
}

function BoardTab() {
  const [kindFilter, setKindFilter] = useState<FeedbackKindFilterState['kind']>('')
  const [sort, setSort] = useState<FeedbackSort>('recent')

  const { items: allItems, isLoading, error } = useAdminFeedbackItems({ sort })
  const { updateItem, isUpdating } = useAdminUpdateFeedbackItem()
  const { retryLinearSync, isRetrying } = useAdminRetryLinearSync()

  const counts = useMemo(() => computeFeedbackKindCounts(allItems), [allItems])
  const filterCtx = useMemo(() => ({ counts }), [counts])

  const feedbackFilters: FeedbackKindFilterState = useMemo(
    () => ({ search: '', kind: kindFilter }),
    [kindFilter],
  )

  const onFiltersChange = useCallback((next: FeedbackKindFilterState) => {
    setKindFilter(next.kind)
  }, [])

  const clearFilters = useCallback(() => {
    setKindFilter('')
    setSort('recent')
  }, [])

  const { filterBar } = useListFilterBar({
    listId: 'adminFeedback',
    definitions: FEEDBACK_KIND_FILTER_DEFINITIONS,
    filters: feedbackFilters,
    onFiltersChange,
    ctx: filterCtx,
    onClearAll: clearFilters,
    hasActiveExtras: sort !== 'recent',
    toolbar: {
      sortMenu: (
        <ListSortMenu
          options={[...FEEDBACK_SORT_OPTIONS]}
          value={sort}
          onValueChange={(value) => setSort(value as FeedbackSort)}
          ariaLabel="Sort feedback"
        />
      ),
    },
  })

  const items = useMemo(() => {
    if (!kindFilter || kindFilter === 'all') return allItems
    return allItems.filter((item) => item.kind === kindFilter)
  }, [allItems, kindFilter])

  useEffect(() => {
    if (kindFilter && kindFilter !== 'all' && (counts[kindFilter] ?? 0) === 0) {
      setKindFilter('')
    }
  }, [kindFilter, counts])

  const handleStatusChange = (item: FeedbackAdminItem, status: FeedbackStatus) => {
    updateItem(
      { itemId: item.id, data: { status } },
      {
        onSuccess: () => toast.success(`Status set to ${formatFeedbackStatus(status).toLowerCase()}`),
        onError: (err: Error) => toast.error(err.message || 'Failed to update status'),
      },
    )
  }

  const handleRetryLinear = (item: FeedbackAdminItem) => {
    retryLinearSync(item.id, {
      onSuccess: () => toast.success('Linear sync queued'),
      onError: (err: Error) => toast.error(err.message || 'Failed to retry Linear sync'),
    })
  }

  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="text-destructive">Error loading feedback: {(error as Error).message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {filterBar}
      {isLoading ? (
        <DashboardSectionSkeleton cards={4} grid />
      ) : items.length === 0 ? (
        <Card>
          <CardContent>
            <PaneListEmptyState
              icon={<Megaphone aria-hidden />}
              title="No feedback yet"
              description="Tester submissions will appear here for triage."
            />
          </CardContent>
        </Card>
      ) : (
        <PaneResponsiveCardGrid>
          {items.map((item) => (
            <AdminFeedbackCard
              key={item.id}
              item={item}
              onStatusChange={handleStatusChange}
              onRetryLinear={handleRetryLinear}
              isUpdating={isUpdating}
              isRetrying={isRetrying}
            />
          ))}
        </PaneResponsiveCardGrid>
      )}
    </div>
  )
}

function RatingsTab() {
  const { ratings, isLoading, error } = useAdminMessageRatings()
  const { promoteRating, isPromoting } = usePromoteMessageRating()

  const handlePromote = (rating: MessageRatingAdmin) => {
    const title =
      rating.comment?.trim() ||
      (rating.score < 0.5 ? 'Thumbs-down rating' : 'Thumbs-up rating')
    promoteRating(
      { ratingId: rating.id, title: title.slice(0, 120) },
      {
        onSuccess: () => toast.success('Rating promoted to the shared board'),
        onError: (err: Error) => toast.error(err.message || 'Failed to promote rating'),
      },
    )
  }

  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="text-destructive">Error loading ratings: {(error as Error).message}</p>
      </div>
    )
  }

  if (isLoading) {
    return <DashboardSectionSkeleton cards={4} grid />
  }

  if (ratings.length === 0) {
    return (
      <Card>
        <CardContent>
          <PaneListEmptyState
            icon={<ThumbsUp aria-hidden />}
            title="No message ratings yet"
            description="Thumbs up/down on agent messages appear here. Promote notable ones to the board."
          />
        </CardContent>
      </Card>
    )
  }

  return (
    <PaneResponsiveCardGrid>
      {ratings.map((rating) => (
        <RatingCard
          key={rating.id}
          rating={rating}
          onPromote={handlePromote}
          isPromoting={isPromoting}
        />
      ))}
    </PaneResponsiveCardGrid>
  )
}

export function AdminFeedbackPage() {
  const basePath = useAdminBasePath()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<'board' | 'ratings'>('board')

  const { title, description } = getPageHeader(
    adminPath(basePath, 'feedback'),
    'Feedback triage',
    'Review board items and message ratings',
  )

  return (
    <PageContainer>
      <PageHeaderStack>
        <PageHeader
          leading={<PageHeaderLeading />}
          title={title}
          description={description}
          icon={<Megaphone className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              primary={
                <PageHeaderActionPrimary
                  type="button"
                  onClick={() =>
                    navigateWithThreadPreservation(
                      navigate,
                      adminPath(basePath, 'feedback/prompts'),
                      searchParams,
                    )
                  }
                >
                  <Settings2 className="h-4 w-4" aria-hidden />
                  Manage prompts
                </PageHeaderActionPrimary>
              }
            />
          }
        />
      </PageHeaderStack>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'board' | 'ratings')}>
        <TabsList>
          <TabsTrigger value="board">Board</TabsTrigger>
          <TabsTrigger value="ratings">Ratings</TabsTrigger>
        </TabsList>
        <TabsContent value="board" className="mt-4">
          <BoardTab />
        </TabsContent>
        <TabsContent value="ratings" className="mt-4">
          <RatingsTab />
        </TabsContent>
      </Tabs>
    </PageContainer>
  )
}
